import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/Tabs';
import { ExperiencesList } from '@/components/creator/ExperiencesList';
import { StripeConnect } from '@/components/creator/StripeConnect';
import { BrandingSettings } from '@/components/creator/BrandingSettings';
import { ProfileEditor } from '@/components/creator/ProfileEditor';
import { BookingRequests } from '@/components/creator/BookingRequests';
import { CreatorBusinessMetrics } from '@/components/creator/CreatorBusinessMetrics';
import { CreatorExperienceTable } from '@/components/creator/CreatorExperienceTable';
import { CreatorCustomerManagement } from '@/components/creator/CreatorCustomerManagement';
import { useAuth } from '@/lib/auth';
import { useExperienceStore } from '@/lib/stores/experience-store';
import { Button } from '@/components/ui/Button';
import { 
  Calendar, 
  Users, 
  DollarSign, 
  TrendingUp, 
  Eye,
  Download,
  Settings,
  Plus,
  Star,
  Palette,
  CreditCard,
  User,
  BarChart3
} from 'lucide-react';
import { Link } from 'react-router-dom';

export function CreatorDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { loadExperiences } = useExperienceStore();

  useEffect(() => {
    loadExperiences();
  }, [loadExperiences]);

  if (!user || user.role !== 'creator') {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <div className="text-center">
          <p className="text-sand-400 mb-4">You must be an approved creator to view this page.</p>
          <Button onClick={() => navigate('/apply/creator')}>
            Apply to Become a Creator
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 pb-12 bg-earth-900">
      <div className="container mx-auto px-4">
        <div className="flex flex-col sm:flex-row sm:items-center gap-4 mb-8">
          <div className="flex items-center gap-4">
            <div className="h-12 w-12 rounded-full bg-gradient-to-br from-sand-400/20 to-earth-600/20 flex items-center justify-center">
              <BarChart3 className="h-6 w-6 text-sand-400" />
            </div>
            <div>
              <h1 className="text-3xl font-display text-sand-100">Business Dashboard</h1>
              <p className="text-sand-400">Your experience business at a glance</p>
            </div>
          </div>
          <Button 
            variant="outline" 
            className="sm:ml-auto w-full sm:w-auto"
            onClick={() => navigate(`/creators/${user.id}`)}
          >
            View Public Profile
          </Button>
        </div>

        <Tabs defaultValue="overview" className="space-y-6">
          <div className="overflow-x-auto -mx-4 px-4">
            <TabsList className="w-full min-w-max bg-earth-800/50 border border-earth-700/50">
              <TabsTrigger value="overview" className="flex-1">
                <TrendingUp className="h-4 w-4 mr-2" />
                Overview
              </TabsTrigger>
              <TabsTrigger value="experiences" className="flex-1">
                <Star className="h-4 w-4 mr-2" />
                Experiences
              </TabsTrigger>
              <TabsTrigger value="customers" className="flex-1">
                <Users className="h-4 w-4 mr-2" />
                Customers
              </TabsTrigger>
              <TabsTrigger value="bookings" className="flex-1">
                <Calendar className="h-4 w-4 mr-2" />
                Bookings
              </TabsTrigger>
              <TabsTrigger value="settings" className="flex-1">
                <Settings className="h-4 w-4 mr-2" />
                Settings
              </TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="overview">
            <CreatorBusinessMetrics />
          </TabsContent>

          <TabsContent value="experiences">
            <div className="space-y-6">
              <CreatorExperienceTable />
              <div className="bg-earth-800/30 p-6 rounded-lg border border-earth-700/30">
                <h3 className="text-lg font-display text-sand-100 mb-4">Experience Management</h3>
                <ExperiencesList />
              </div>
            </div>
          </TabsContent>

          <TabsContent value="customers">
            <CreatorCustomerManagement />
          </TabsContent>

          <TabsContent value="bookings">
            <div className="space-y-6">
              <div className="bg-earth-800/30 p-6 rounded-lg border border-earth-700/30">
                <h3 className="text-lg font-display text-sand-100 mb-4">Booking Requests</h3>
                <BookingRequests />
              </div>
              
              {/* Coming Soon Sections */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-earth-800/30 p-6 rounded-lg border border-earth-700/30 opacity-60">
                  <h3 className="text-sand-100 font-medium mb-2">Refund Management</h3>
                  <p className="text-sand-400 text-sm mb-4">Process refunds and manage cancellations</p>
                  <Button size="sm" disabled className="opacity-50">
                    Coming Soon
                  </Button>
                </div>
                
                <div className="bg-earth-800/30 p-6 rounded-lg border border-earth-700/30 opacity-60">
                  <h3 className="text-sand-100 font-medium mb-2">Ticket Transfer Requests</h3>
                  <p className="text-sand-400 text-sm mb-4">Approve guest ticket transfers</p>
                  <Button size="sm" disabled className="opacity-50">
                    Coming Soon
                  </Button>
                </div>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="settings">
            <div className="space-y-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-earth-800/30 p-6 rounded-lg border border-earth-700/30">
                  <h3 className="text-lg font-display text-sand-100 mb-4">Business Profile</h3>
                  <ProfileEditor />
                </div>
                
                <div className="bg-earth-800/30 p-6 rounded-lg border border-earth-700/30">
                  <h3 className="text-lg font-display text-sand-100 mb-4">Branding</h3>
                  <BrandingSettings />
                </div>
              </div>
              
              <div className="bg-earth-800/30 p-6 rounded-lg border border-earth-700/30">
                <h3 className="text-lg font-display text-sand-100 mb-4">Payment Setup</h3>
                <StripeConnect />
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}